import {
  Box,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  Tooltip,
} from '@mui/joy';
import { FC, JSX, ReactNode, useEffect, useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { LinearProgress } from '@mui/material';
import { useDispatch } from 'react-redux';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { Loader } from '../Loader/Loader';
import './FileDetailPanelSidebar.scss';
import { useTranslation } from 'react-i18next';

type FileData = {
  id: number;
  fileName: string;
  useGetFileDataQuery: any;
  addFile: any;
  formatCompactNumber?: (value: number) => string;
  /**
   * Extra sections appended below Contract Information. A slot rather than a
   * data prop so the lib keeps knowing nothing about the app's domain APIs -
   * the caller renders whatever it wants there.
   */
  children?: ReactNode;
};

/**
 * Splits a filename into its display name and extension. `lastIndexOf` rather
 * than `split('.')` because a name with no dot has no extension - it is not
 * its own type - and a leading dot is a hidden file, not an extension either.
 */
const splitFileName = (name?: string) => {
  const dot = name ? name.lastIndexOf('.') : -1;
  if (!name || dot <= 0) return { base: name ?? '', ext: '' };
  return { base: name.slice(0, dot), ext: name.slice(dot + 1) };
};

/**
 * Temporarily hides the DORA compliance summary card.
 * The component is intentionally left in place - flip to `true` to restore.
 */
const SHOW_DORA_COMPLIANCE = false;

// Mock DORA compliance data - replace with real API call when backend is ready
const getDORASummary = (_fileId: number) => ({
  overallScore: 62,
  compliantCount: 8,
  totalRequirements: 13,
  criticalGaps: 3,
  topGaps: [
    'Incident notification timelines not defined',
    'Audit rights insufficient',
    'Exit assistance provisions missing',
  ],
});

/* -------------------------------------------------------------------------- */
/* Presentational building blocks                                             */
/* -------------------------------------------------------------------------- */

/**
 * Collapsible block. Rolled by hand rather than with Joy's Accordion so the
 * panel can stay flat and tight - the Accordion nests its own surface, padding
 * and min-height, which is most of what made the old panel bulky.
 */
const Disclosure: FC<{
  header: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
  className?: string;
}> = ({ header, children, defaultOpen = false, className }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Box className={`disclosure${className ? ` ${className}` : ''}`}>
      <Box
        component="button"
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="disclosure__trigger"
      >
        <Box className="disclosure__header">{header}</Box>
        <ExpandMoreIcon
          className={`disclosure__chevron${
            open ? ' disclosure__chevron--open' : ''
          }`}
        />
      </Box>
      {open && <Box className="disclosure__body">{children}</Box>}
    </Box>
  );
};

/** Section label that heads a group of rows. */
const SectionLabel: FC<{ children: ReactNode; count?: number }> = ({
  children,
  count,
}) => (
  <Box className="section-label">
    <span>{children}</span>
    {count !== undefined && (
      <span className="section-label__count">{count}</span>
    )}
  </Box>
);

/**
 * One label/value line. Label and value share a row and the value flows
 * underneath only when it is too long - far denser than the old stacked pair.
 */
const DetailField: FC<{ label: string; value?: ReactNode }> = ({
  label,
  value,
}) => {
  if (value === undefined || value === null || value === '') return null;
  return (
    <Box className="detail-row">
      <Typography level="body-xs" className="detail-row__label">
        {label}
      </Typography>
      <Typography level="body-xs" className="detail-row__value">
        {value}
      </Typography>
    </Box>
  );
};

/* -------------------------------------------------------------------------- */
/* Section components                                                         */
/* -------------------------------------------------------------------------- */

const PartyRow: FC<{ party: any }> = ({ party }) => {
  const { t } = useTranslation();
  return (
    <Disclosure
      className="party"
      header={
        <>
          <Typography level="body-xs" className="party__role">
            {party.role || t('Party')}
          </Typography>
          <Typography level="body-sm" className="party__name">
            {party.name}
          </Typography>
        </>
      }
    >
      <DetailField label={t('Hierarchy')} value={party.hierarchy_level} />
      <DetailField label={t('Info')} value={party.info} />
      <DetailField label={t('Address')} value={party.address?.full_address} />
      <DetailField
        label={t('Aliases')}
        value={party.aliases?.length ? party.aliases.join(', ') : undefined}
      />
    </Disclosure>
  );
};

const PartiesSection: FC<{ parties: any[] }> = ({ parties }) => {
  const { t } = useTranslation();
  return (
    <Box className="panel-section">
      <SectionLabel count={parties.length}>{t('Parties')}</SectionLabel>
      <Box className="party-list">
        {parties.map((party, idx) => (
          <PartyRow key={idx} party={party} />
        ))}
      </Box>
    </Box>
  );
};

const DoraStat: FC<{ label: string; value: ReactNode; icon: ReactNode }> = ({
  label,
  value,
  icon,
}) => (
  <Grid xs={4}>
    <Box className="dora-stat">
      <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
      <Typography level="body-sm" fontWeight={700} className="dora-stat__value">
        {icon}
        {value}
      </Typography>
    </Box>
  </Grid>
);

const DoraComplianceCard: FC<{ fileId: number }> = ({ fileId }) => {
  const { t } = useTranslation();
  const doraSummary = getDORASummary(fileId);
  const compliancePercentage =
    (doraSummary.compliantCount / doraSummary.totalRequirements) * 100;
  const status =
    compliancePercentage >= 80
      ? 'success'
      : compliancePercentage >= 60
      ? 'warning'
      : 'danger';

  return (
    <Box className="panel-section">
      <SectionLabel>{t('DORA Compliance')}</SectionLabel>

      <Box className="dora-score-row">
        <Typography level="body-xs" className="detail-row__label">
          {t('Compliance Score')}
        </Typography>
        <Typography level="title-sm" className="gradient-text">
          {Math.round(compliancePercentage)}%
        </Typography>
      </Box>

      <LinearProgress
        variant="determinate"
        value={Math.max(0, Math.min(100, compliancePercentage))}
        className={`dora-progress dora-progress--${status}`}
      />

      <Grid container spacing={0.75} className="dora-stats">
        <DoraStat
          label={t('Compliant')}
          icon={<CheckCircleIcon sx={{ fontSize: 14, color: 'success.500' }} />}
          value={doraSummary.compliantCount}
        />
        <DoraStat
          label={t('Non-Compliant')}
          icon={<ErrorIcon sx={{ fontSize: 14, color: 'warning.500' }} />}
          value={doraSummary.totalRequirements - doraSummary.compliantCount}
        />
        <DoraStat
          label={t('Critical Gaps')}
          icon={<WarningIcon sx={{ fontSize: 14, color: 'danger.500' }} />}
          value={doraSummary.criticalGaps}
        />
      </Grid>

      {doraSummary.criticalGaps > 0 && (
        <Box>
          <Typography level="body-xs" className="dora-gaps__title">
            {t('Top Critical Gaps:')}
          </Typography>
          <Box component="ul" className="dora-gaps">
            {doraSummary.topGaps.map((gap, idx) => (
              <li key={idx}>{t(gap)}</li>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

/* -------------------------------------------------------------------------- */
/* Panel                                                                      */
/* -------------------------------------------------------------------------- */

export const FileDetailPanel: FC<FileData> = ({
  id,
  fileName,
  useGetFileDataQuery,
  addFile,
  formatCompactNumber = (value: number) => value.toString(),
  children,
}): JSX.Element | null => {
  const { t } = useTranslation();
  const [enablePolling, setEnablePolling] = useState(true);
  const dispatch = useDispatch();

  const { data, error, isLoading } = useGetFileDataQuery(id ?? skipToken, {
    pollingInterval: enablePolling ? 5000 : undefined,
    refetchOnMountOrArgChange: true,
  });

  const fileData = data?.data;

  // The API returns two shapes for the same file:
  //  - "old": contract fields nested under `processed_data`, metadata under
  //    `structured_data.metadata`.
  //  - "new": `processed_data` is null, the same fields are hoisted onto
  //    `fileData`, and there is an extra top-level `metadata`.
  // Read from both so every field renders regardless of which shape arrives.
  const processed_data = fileData?.processed_data;
  const structuredMeta = fileData?.structured_data?.metadata;
  const topMeta = fileData?.metadata;
  const parties = fileData?.structured_data?.parties;

  const status = processed_data?.status ?? fileData?.status;
  const inProgress = status?.code === 'processing';

  const fileType = (processed_data?.file_title || fileData?.filename)
    ?.split('.')
    .pop();
  const isContract = (processed_data?.contract_analysis_results ?? [])[0]
    ?.is_contract;

  // contract_type is `{ name }` (old) or a plain string (new).
  const rawContractType =
    processed_data?.contract_type ?? fileData?.contract_type;
  const contractCategory =
    (typeof rawContractType === 'string'
      ? rawContractType
      : rawContractType?.name) || structuredMeta?.contract_type;

  // Contract Information fields
  // The effective date is the extraction's own `effective_date`. `contract_date`
  // and `commencement_date` are the date the contract was made, which is a
  // different date - they stay as fallbacks for payloads carrying no metadata.

  console.log('structuredMeta', structuredMeta);
  const effectiveDate =
    structuredMeta?.effective_date ||
    topMeta?.effective_date ||
    processed_data?.contract_date ||
    fileData?.contract_date ||
    structuredMeta?.commencement_date;
  const contractEndDate =
    topMeta?.contract_end_date || structuredMeta?.contract_end_date;
  const contractAmount = structuredMeta?.contract_amount;
  const uploadDate = fileData?.contract_collection_date;
  const hasContractInfo = !!(
    effectiveDate ||
    contractCategory ||
    contractEndDate ||
    contractAmount ||
    uploadDate ||
    fileType
  );

  const hasParties = Array.isArray(parties) && parties.length > 0;

  // The badge carries the format, so the name sheds its extension.
  const { base: baseName, ext } = splitFileName(fileName);

  const statusColor = inProgress ? 'neutral' : error ? 'danger' : 'success';
  const statusText = inProgress
    ? t('In Progress')
    : error
    ? t('Error')
    : status?.name;

  // Sync the file slice with the latest fileData.
  useEffect(() => {
    if (fileData?.id) {
      dispatch(addFile({ ...fileData, id: String(fileData.id) }));
    }
  }, [fileData, dispatch, addFile]);

  // Stop polling once processing completes.
  useEffect(() => {
    setEnablePolling(inProgress);
  }, [inProgress, id]);

  if (isLoading) {
    return <Loader />;
  }

  if (fileData?.is_unknown) {
    return null;
  }

  return (
    <Box className="FileDetailPanel">
      {/* File identity: a format badge, the name with its extension stripped
          (the badge already says it), and the state of the document. */}
      <Box className="file-head">
        <Box className="file-badge" aria-hidden="true">
          {ext ? (
            <span className="file-badge__ext">
              {ext.slice(0, 4).toUpperCase()}
            </span>
          ) : (
            <InsertDriveFileOutlinedIcon className="file-badge__icon" />
          )}
        </Box>

        <Box className="file-head__text">
          <Tooltip title={fileName} placement="bottom-start" size="sm">
            <Typography className="file-head__name">{baseName}</Typography>
          </Tooltip>

          <Box className="file-meta">
            <Chip
              size="sm"
              variant="soft"
              color={statusColor}
              className="file-meta__status"
              startDecorator={
                inProgress ? (
                  <CircularProgress
                    thickness={2}
                    sx={{ '--CircularProgress-size': '10px' }}
                  />
                ) : undefined
              }
            >
              {statusText}
            </Chip>
            {!error && !inProgress && isContract !== undefined && (
              <Chip
                size="sm"
                variant="soft"
                color={isContract ? 'primary' : 'neutral'}
                className="file-meta__status"
              >
                {isContract ? t('Contract') : t('Not a contract')}
              </Chip>
            )}
          </Box>
        </Box>
      </Box>

      {!error && !inProgress && (
        <Box className="FileDetailPanel__body">
          {hasParties && <PartiesSection parties={parties} />}

          {hasContractInfo && (
            <Box className="panel-section">
              <SectionLabel>{t('Contract Information')}</SectionLabel>
              <DetailField label={t('Category')} value={contractCategory} />
              <DetailField label={t('Effective')} value={effectiveDate} />
              <DetailField label={t('Ends')} value={contractEndDate} />
              <DetailField
                label={t('Amount')}
                value={
                  contractAmount
                    ? formatCompactNumber(contractAmount)
                    : undefined
                }
              />
              <DetailField label={t('Uploaded')} value={uploadDate} />
            </Box>
          )}

          {SHOW_DORA_COMPLIANCE && <DoraComplianceCard fileId={id} />}

          {children}
        </Box>
      )}
    </Box>
  );
};
